import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  BookOpen, 
  Bookmark,
  BookmarkCheck,
  Loader2,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-granth`;

export default function AIGranthBookmarksPage({ user }) {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchBookmarks();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/bookmarks?token=${token}`);
      setBookmarks(response.data.bookmarks || []);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (verseId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/bookmarks/${verseId}?token=${token}`);
      setBookmarks(prev => prev.filter(b => b.bookmark.verse_id !== verseId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <div className="text-center">
          <Bookmark className="w-16 h-16 text-amber-300 mx-auto mb-4" />
          <p className="text-amber-700 mb-4">Please login to view your bookmarks</p>
          <button
            onClick={() => navigate('/auth')}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Header */}
      <div className="bg-white border-b border-amber-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/ai-granth')}
              className="p-2 hover:bg-amber-50 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-amber-800" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-amber-900">My Bookmarks</h1>
              <p className="text-sm text-amber-600">{bookmarks.length} saved verses</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
          </div>
        ) : bookmarks.length === 0 ? (
          <div className="text-center py-16">
            <BookmarkCheck className="w-16 h-16 text-amber-300 mx-auto mb-4" />
            <p className="text-amber-700 mb-2">No bookmarks yet</p>
            <p className="text-sm text-amber-500 mb-6">
              Save verses while reading to find them here
            </p>
            <Link
              to="/ai-granth"
              className="inline-flex items-center gap-2 px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
            >
              <BookOpen className="w-4 h-4" />
              Browse Scriptures
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {bookmarks.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-5 border border-amber-100 hover:shadow-md transition-all"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <Link
                    to={`/ai-granth/book/${item.bookmark.book_id}/chapter/${item.bookmark.chapter_id}`}
                    className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-800"
                  >
                    <BookOpen className="w-4 h-4" />
                    <span>{item.book?.title || 'Scripture'}</span>
                    <span>•</span>
                    <span>Chapter {item.chapter?.chapter_number}</span>
                    <span>•</span>
                    <span>Verse {item.verse?.verse_number}</span>
                  </Link>

                  <button
                    onClick={() => removeBookmark(item.bookmark.verse_id)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove bookmark"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Verse Content */}
                <Link to={`/ai-granth/book/${item.bookmark.book_id}/chapter/${item.bookmark.chapter_id}`}>
                  <p 
                    className="text-amber-900 font-medium mb-2 hover:text-amber-700"
                    style={{ fontFamily: 'Noto Serif Devanagari, serif' }}
                  >
                    {item.verse?.original_text?.split('\n').slice(0, 2).join('\n')}
                  </p>

                  {item.verse?.english_meaning && (
                    <p className="text-sm text-amber-700 line-clamp-2">
                      {item.verse.english_meaning}
                    </p>
                  )}
                </Link>

                {/* Timestamp */}
                <p className="text-xs text-amber-400 mt-3">
                  Saved {new Date(item.bookmark.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
